const hexCharacters = "a-f\\d";
const match3or4Hex = `#?[${hexCharacters}]{3}[${hexCharacters}]?`;
const match6or8Hex = `#?[${hexCharacters}]{6}([${hexCharacters}]{2})?`;
const nonHexChars = new RegExp(`[^#${hexCharacters}]`, "gi");
const validHexSize = new RegExp(`^${match3or4Hex}$|^${match6or8Hex}$`, "i");
export interface RgbaObject {
    red: number;
    green: number;
    blue: number;
    alpha: number;
}

export type RgbaTuple = [red: number, green: number, blue: number, alpha: number];

interface BaseOptions {
    readonly alpha?: number;
}

// Internal shared function that does the actual conversion
function parseHex(hex: string, alpha?: number) {
    if (typeof hex !== "string" || nonHexChars.test(hex) || !validHexSize.test(hex)) {
        throw new TypeError("Expected a valid hex string");
    }

    hex = hex.replace(/^#/, "");
    let alphaFromHex = 1;

    if (hex.length === 8) {
        alphaFromHex = Number.parseInt(hex.slice(6, 8), 16) / 255;
        hex = hex.slice(0, 6);
    }

    if (hex.length === 4) {
        alphaFromHex = Number.parseInt(hex.slice(3, 4).repeat(2), 16) / 255;
        hex = hex.slice(0, 3);
    }

    if (hex.length === 3) {
        hex = hex[0] + hex[0] + hex[1] + hex[1] + hex[2] + hex[2];
    }

    const number = Number.parseInt(hex, 16);
    const red = number >> 16;
    const green = (number >> 8) & 255;
    const blue = number & 255;

    // Validate and sanitize the override alpha
    let finalAlpha = alphaFromHex; // Default to hex alpha
    if (typeof alpha === "number" && !isNaN(alpha)) {
        // Clamp alpha to valid range [0, 1]
        finalAlpha = Math.max(0, Math.min(1, alpha));
    }

    // Round alpha to 2 decimal places for cleaner values
    const roundedAlpha = Math.round(finalAlpha * 100) / 100;

    return { red, green, blue, alpha: roundedAlpha };
}

// Default function - returns RGBA object
export function hexToRgb(hex: string, options?: BaseOptions): RgbaObject {
    return parseHex(hex, options?.alpha);
}

// Array format function
export function hexToRgbArray(hex: string, options?: BaseOptions): RgbaTuple {
    const { red, green, blue, alpha } = parseHex(hex, options?.alpha);
    return [red, green, blue, alpha];
}

// CSS format function
export function hexToRgbCss(hex: string, options?: BaseOptions): string {
    const { red, green, blue, alpha } = parseHex(hex, options?.alpha);

    if (alpha === 1) {
        return `rgb(${red}, ${green}, ${blue})`;
    } else {
        return `rgba(${red}, ${green}, ${blue}, ${alpha})`;
    }
}
